import React, { useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "../i18n/I18nProvider";
import { QuoteHolderFormState } from "../types/leadWizard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons";

interface QuoteHolderStepProps {
  initialValues: QuoteHolderFormState;
  selectedHouseholdTypeIcon?: string | null;
  onSubmit: (values: QuoteHolderFormState) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
}

export const QuoteHolderStep: React.FC<QuoteHolderStepProps> = ({
  initialValues,
  selectedHouseholdTypeIcon,
  onSubmit,
  onBack,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] =
    useState<QuoteHolderFormState>(initialValues);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    field: keyof QuoteHolderFormState,
    value: string | boolean
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validate = (): boolean => {
    if (!formValues.address1.trim()) {
      setError(t("quote.holder.errors.address1"));
      return false;
    }
    if (!formValues.city.trim()) {
      setError(t("quote.holder.errors.city"));
      return false;
    }
    if (!formValues.stateCode.trim()) {
      setError(t("quote.holder.errors.stateCode"));
      return false;
    }
    if (!formValues.zipCode.trim()) {
      setError(t("quote.holder.errors.zipCode"));
      return false;
    }
    if (!formValues.ssn.trim()) {
      setError(t("quote.holder.errors.ssn"));
      return false;
    }

    setError(null);
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    try {
      await onSubmit(formValues);
    } catch (err: any) {
      setError(err?.message ?? t("wizard.errors.generic"));
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <button
          type="button"
          className="button button-secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          {t("buttons.back")}
        </button>
      </div>
      <div className="card-body">
        <div className="household-type-selection-header">
          <div>
            <h3 className="card-title">{t("quote.holder.title")}</h3>
            <p className="section-helper">{t("quote.holder.helper")}</p>
          </div>
          {selectedHouseholdTypeIcon && (
            <div className="household-type-selected">
              <span aria-hidden="true" className="household-type-icon">
                {selectedHouseholdTypeIcon}
                <FontAwesomeIcon
                  className="fa-check step-3"
                  icon={faCircleCheck}
                />
              </span>
            </div>
          )}
        </div>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="holderFirstName">
                <Form.Label>{t("quote.holder.firstName")}</Form.Label>
                <Form.Control value={formValues.firstName} disabled readOnly />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="holderLastName">
                <Form.Label>{t("quote.holder.lastName")}</Form.Label>
                <Form.Control value={formValues.lastName} disabled readOnly />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="holderEmail">
                <Form.Label>{t("quote.holder.email")}</Form.Label>
                <Form.Control
                  type="email"
                  value={formValues.email}
                  disabled
                  readOnly
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="holderPhone">
                <Form.Label>{t("quote.holder.phone")}</Form.Label>
                <Form.Control value={formValues.phone} disabled readOnly />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group controlId="holderAddress1" className="mb-3">
            <Form.Label>{t("quote.holder.address1")}</Form.Label>
            <Form.Control
              value={formValues.address1}
              onChange={(event) => handleChange("address1", event.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="holderAddress2" className="mb-3">
            <Form.Label>{t("quote.holder.address2")}</Form.Label>
            <Form.Control
              value={formValues.address2}
              onChange={(event) => handleChange("address2", event.target.value)}
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="holderCity">
                <Form.Label>{t("quote.holder.city")}</Form.Label>
                <Form.Control
                  value={formValues.city}
                  onChange={(event) => handleChange("city", event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="holderStateCode">
                <Form.Label>{t("quote.holder.stateCode")}</Form.Label>
                <Form.Control
                  value={formValues.stateCode}
                  onChange={(event) =>
                    handleChange("stateCode", event.target.value.toUpperCase())
                  }
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="holderZip">
                <Form.Label>{t("quote.holder.zipCode")}</Form.Label>
                <Form.Control
                  value={formValues.zipCode}
                  onChange={(event) =>
                    handleChange("zipCode", event.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="holderStateName">
                <Form.Label>{t("quote.holder.stateName")}</Form.Label>
                <Form.Control
                  value={formValues.stateName ?? ""}
                  onChange={(event) =>
                    handleChange("stateName", event.target.value)
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="holderCounty">
                <Form.Label>{t("quote.holder.countyFips")}</Form.Label>
                <Form.Control
                  value={formValues.countyFips ?? ""}
                  onChange={(event) =>
                    handleChange("countyFips", event.target.value)
                  }
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group controlId="holderSsn" className="mb-3">
            <Form.Label>{t("quote.holder.ssn")}</Form.Label>
            <Form.Control
              value={formValues.ssn}
              onChange={(event) => handleChange("ssn", event.target.value)}
              placeholder="000-00-0000"
            />
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Check
                type="switch"
                id="holderUsesTobacco"
                label={t("quote.holder.usesTobacco")}
                checked={Boolean(formValues.usesTobacco)}
                onChange={(event) =>
                  handleChange("usesTobacco", event.target.checked)
                }
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="holderIsPregnant"
                label={t("quote.holder.isPregnant")}
                checked={Boolean(formValues.isPregnant)}
                onChange={(event) =>
                  handleChange("isPregnant", event.target.checked)
                }
              />
            </Col>
          </Row>

          <div className="form-actions">
            <Button
              type="submit"
              className="button button-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t("quote.holder.saving")}
                </>
              ) : (
                t("buttons.continue")
              )}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};
